import { FC, useEffect, useState } from 'react';
import { CustomButton, CustomInput, CustomModal, CustomTextArea, FileUpload, If, ListDropDown } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import DayPicker from 'components/day-picker/DayPicker';
import { CalendarIcon, UploadIcon } from 'assets/icons';
import { FILE_UPLOAD_SIZE, REGEX } from 'constants/common';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import { AdjustmentSourceType, AdjustmentType } from '../constants';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useUploadAdjustmentReceiptMutation } from '../api';
import { Assignee } from 'containers/jobs/jobs.model';
import classNames from 'classnames';

interface AddAdjustmentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (adjustmentData: CreateAdjustmentRequest) => void;
  type?: AdjustmentType;
  isEdit?: boolean;
  selectedAdjustment?: any;
  sourceType: AdjustmentSourceType;
  assignees: Assignee[];
  isLoading?: boolean;
}

export interface AdjustmentData {
  title: string;
  amount: string;
  date: Date;
  description: string;
  receiptUrl?: string;
  receiptPreview?: string;
  type: AdjustmentType;
  member?: Assignee;
  isLoading?: boolean;
  currentMerchantId?: string;
}

export interface CreateAdjustmentRequest {
  title: string;
  amount: string;
  date: string;
  description: string;
  receiptUrl?: string;
  latestMerchantId?: string;
  currentMerchantId?: string;
}

const initialAdjustmentData: AdjustmentData = {
  title: '',
  amount: '',
  date: new Date(),
  description: '',
  receiptUrl: '',
  receiptPreview: '',
  type: AdjustmentType.EARNING
};

const AddAdjustmentModal: FC<AddAdjustmentModalProps> = ({
  open,
  onClose,
  onSave,
  type,
  isEdit,
  selectedAdjustment,
  sourceType,
  assignees = [],
  isLoading
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [uploadReceipt] = useUploadAdjustmentReceiptMutation();
  const [adjustmentData, setAdjustmentData] = useState<AdjustmentData>(initialAdjustmentData);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  useEffect(() => {
    if (selectedAdjustment) {
      setAdjustmentData({
        ...selectedAdjustment,
        amount: Math.abs(Number(selectedAdjustment.amount)).toString(),
        type: +selectedAdjustment.amount < 0 ? AdjustmentType.DEDUCTION : AdjustmentType.EARNING,
        date: new Date(selectedAdjustment.postedDate),
        description: selectedAdjustment.notes,
        member: {
          id: selectedAdjustment.merchantId,
          name: selectedAdjustment.merchantName
        },
        receiptUrl: selectedAdjustment.receiptKey,
        currentMerchantId: selectedAdjustment.merchantId
      });
      setReceiptPreview(selectedAdjustment.receiptUrl);
    }
    if (type) {
      setAdjustmentData(prev => ({
        ...prev,
        type
      }));
    }
  }, [selectedAdjustment, type]);

  const handleInputChange = (field: keyof AdjustmentData, value: string | Date) => {
    setAdjustmentData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAmountChange = (value: string) => {
    if (REGEX.amount.test(value)) {
      handleInputChange('amount', value);
    }
  };

  const handleSave = async () => {
    const payload: CreateAdjustmentRequest = {
      title: adjustmentData.title.trim(),
      amount:
        adjustmentData.type === AdjustmentType.EARNING
          ? adjustmentData.amount.trim()
          : `-${adjustmentData.amount.trim()}`,
      date: adjustmentData.date.toISOString(),
      description: adjustmentData.description.trim(),
      receiptUrl: adjustmentData.receiptUrl || null,
      latestMerchantId: adjustmentData.member?.id,
      currentMerchantId: adjustmentData.currentMerchantId || null
    };

    if (receiptFile) {
      setIsUploadingReceipt(true);
      try {
        const response = await uploadReceipt({
          teamId: currentTeam.id,
          image: receiptFile
        }).unwrap();

        payload.receiptUrl = response.imageUrl;
        onSave(payload);
      } catch (error) {
        // Error handling is done by the API's extraOptions
      } finally {
        setIsUploadingReceipt(false);
      }
    } else {
      onSave(payload);
    }

    resetForm();
  };

  const resetForm = () => {
    setAdjustmentData(initialAdjustmentData);
    setReceiptFile(null);
    setReceiptPreview(null);
    onClose();
  };

  const isFormValid = () => {
    return (
      adjustmentData.title?.trim() !== '' &&
      adjustmentData.amount?.trim() !== '' &&
      (sourceType !== AdjustmentSourceType.JOB || adjustmentData.member?.id)
    );
  };

  const handleTeamChange = (value: Assignee) => {
    setAdjustmentData(prev => ({
      ...prev,
      member: value
    }));
  };

  const handleRemovePreview = () => {
    setReceiptPreview(null);
    setAdjustmentData(prev => ({
      ...prev,
      receiptUrl: null
    }));
  };

  return (
    <CustomModal open={open} width="md:w-full max-w-[914px]">
      <div className="py-6 px-[30px]">
        <div className="flex items-center justify-between pb-6">
          <div className="text-xl font-bold text-heading">{isEdit ? 'Edit adjustment' : 'Add adjustment'}</div>
          <FormCloseReactIcon
            id="add-adjustment-modal-close"
            height="24"
            width="24"
            className="cursor-pointer"
            onClick={onClose}
          />
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-1 grid gap-4">
            <div>
              <label className="mb-2 block text-sbase font-semibold text-primaryText">Title</label>
              <CustomInput
                id="adjustment-title"
                name="title"
                placeholder="Enter title"
                value={adjustmentData.title}
                onChange={value => handleInputChange('title', value)}
                className="h-[48px] w-full"
                maxLength={30}
              />
            </div>

            <div>
              <label className="mb-2 block text-sbase font-semibold text-primaryText">Amount</label>
              <div className="flex w-full items-center justify-between gap-2 rounded bg-secondaryBtn pl-3">
                <div
                  className="flex w-[72px] shrink-0 flex-row items-center 
                  gap-2 rounded-md text-sbase font-semibold text-primaryText">
                  <TooltipWrapper tooltip="Earning" className="h-7 w-1/2">
                    <div
                      onClick={() => handleInputChange('type', AdjustmentType.EARNING)}
                      className={`${
                        adjustmentData.type === AdjustmentType.EARNING ? 'border-primaryBtn' : 'border-borderGray'
                      } flex h-full w-full cursor-pointer items-center justify-center rounded-md border bg-primary`}>
                      <div
                        className={classNames('text-xl', {
                          'text-primaryBtn': adjustmentData.type === AdjustmentType.EARNING
                        })}>
                        +
                      </div>
                    </div>
                  </TooltipWrapper>

                  <TooltipWrapper tooltip="Deduction" className="h-7 w-1/2">
                    <div
                      onClick={() => handleInputChange('type', AdjustmentType.DEDUCTION)}
                      className={`${
                        adjustmentData.type === AdjustmentType.DEDUCTION ? 'border-primaryBtn' : 'border-borderGray'
                      } flex h-full w-full cursor-pointer items-center justify-center rounded-md border bg-primary`}>
                      <div
                        className={classNames('text-xl h-0.5 w-2.5', {
                          'bg-primaryBtn': adjustmentData.type === AdjustmentType.DEDUCTION,
                          'bg-primaryText': adjustmentData.type !== AdjustmentType.DEDUCTION
                        })}
                      />
                    </div>
                  </TooltipWrapper>
                </div>
                <CustomInput
                  id="adjustment-amount"
                  name="amount"
                  placeholder="0.00"
                  value={adjustmentData.amount}
                  onChange={handleAmountChange}
                  className="h-12 w-full pl-7"
                  prefixWrapperStyle="left-3 text-accentText"
                  prefix="$"
                  wrapperClassName="w-full"
                />
              </div>
            </div>

            <If condition={sourceType === AdjustmentSourceType.JOB}>
              <div>
                <label className="mb-2 block text-sbase font-semibold text-primaryText">Team</label>
                <ListDropDown
                  id="team-dropdown"
                  items={assignees || []}
                  selectedItem={adjustmentData.member}
                  setSelectedItem={handleTeamChange}
                  buttonStyles="bg-secondaryBtn h-[50px] text-[17px] font-semibold text-primaryText cursor-pointer pl-5"
                  placeholder="Select member"
                  spanStyles={`font-semibold text-[15px] leading-[21px] ${
                    adjustmentData.member ? 'text-primaryText' : 'text-accentText opacity-70'
                  }`}
                  itemStyle="font-normal text-[15px] tracking-[-0.3px] leading-[28px]"
                />
              </div>
            </If>

            <div>
              <label className="mb-2 block text-sbase font-semibold text-primaryText">Date</label>
              <DayPicker
                prefixIcon={<CalendarIcon className="path-fill-current h-[18px] w-[18px] text-accentText" />}
                date={adjustmentData.date}
                onSelect={date => handleInputChange('date', date)}
                labelClassName="font-semibold text-sbase text-primaryText"
                showPastDates={true}
              />
            </div>

            <div>
              <label className="mb-2 block text-sbase font-semibold text-primaryText">Description</label>
              <CustomTextArea
                id="adjustment-description"
                name="description"
                placeholder="Enter description"
                value={adjustmentData.description}
                onChange={value => handleInputChange('description', value)}
                className="h-[80px] w-full"
                maxLength={1000}
              />
            </div>
          </div>

          <div className="col-span-1">
            <label className="mb-2 block text-sbase font-semibold text-primaryText">Receipt</label>
            <FileUpload
              icon="RED_TRASH_ICON"
              id="adjustment-receipt-upload"
              isUploadInProgress={isUploadingReceipt}
              prefill={false}
              setFile={setReceiptFile}
              filePreview={receiptPreview}
              setPreview={setReceiptPreview}
              className="h-[calc(100%-1.75rem)] border-borderGray bg-secondaryBtn"
              FileUploadIcon={UploadIcon}
              supportedFileTypes={{
                'image/jpeg': [],
                'image/png': [],
                'image/webp': []
              }}
              allowMultiple={false}
              maxFileSize={FILE_UPLOAD_SIZE.SIZE_8_MB_BYTES}
              description={
                <span>
                  Drag and drop your receipt here
                  <span className="mt-2.5 block">or click to upload image</span>
                </span>
              }
              subText=""
              removePreview={handleRemovePreview}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <CustomButton
            id="cancel-adjustment-button"
            type={ButtonType.SECONDARY}
            className="h-[38px] px-5"
            onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            id="save-adjustment-button"
            className="h-[38px] px-5"
            disabled={!isFormValid()}
            onClick={handleSave}
            isLoading={isLoading}>
            Save
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default AddAdjustmentModal;
